'use client';

import { useTranslations, useLocale } from 'next-intl';
import {
  Pencil,
  Trash2,
  UserPlus,
  Shield,
  Users,
  CopyPlus,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import { RoleColorBadge } from './role-color-badge';
import { getRoleDisplayName } from '../mappers/role.mapper';
import type { Role } from '../types';

interface RoleCardProps {
  role: Role;
  onAssign: (role: Role) => void;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  onDuplicate: (role: Role) => void;
  onSelectRole: (role: Role) => void;
  isDuplicating?: boolean;
}

export function RoleCard({
  role,
  onAssign,
  onEdit,
  onDelete,
  onDuplicate,
  onSelectRole,
  isDuplicating
}: RoleCardProps) {
  const t = useTranslations('roles');
  const locale = useLocale();

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4'>
          <RoleColorBadge
            name={getRoleDisplayName(role, locale)}
            color={role.color}
            onClick={() => onSelectRole(role)}
          />
        </div>

        <div className='mb-4 flex items-center gap-4'>
          <div className='flex items-center gap-1.5 text-sm'>
            <Shield className='text-muted-foreground h-4 w-4' />
            <span className='text-muted-foreground'>
              {t('permissionsCount')}:
            </span>
            <span className='font-medium'>{role.permissions.length}</span>
          </div>
          <div className='flex items-center gap-1.5 text-sm'>
            <Users className='text-muted-foreground h-4 w-4' />
            <span className='text-muted-foreground'>{t('usersCount')}:</span>
            <span className='font-medium'>{role.users_count}</span>
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['roles.create']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onDuplicate(role)}
              disabled={isDuplicating}
              title={t('duplicateRole')}
            >
              {isDuplicating ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <CopyPlus className='h-4 w-4' />
              )}
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['roles.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onAssign(role)}
            >
              <UserPlus className='h-4 w-4' />
            </Button>
            {role.can_edit && (
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                onClick={() => onEdit(role)}
              >
                <Pencil className='h-4 w-4' />
              </Button>
            )}
          </PermissionGate>
          {role.can_delete && (
            <PermissionGate permissions={['roles.delete']}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                onClick={() => onDelete(role)}
              >
                <Trash2 className='text-destructive h-4 w-4' />
              </Button>
            </PermissionGate>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
